import React, { useEffect, useState } from 'react';
import { Plus, MessageSquare, Calendar, Tag, Link2, X, User as UserIcon, Lock } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../lib/api';
import { toast } from 'sonner';
import EmployeePicker from './EmployeePicker';
import LinkedEntityPicker from './LinkedEntityPicker';
import { useAuth } from '../contexts/AuthContext';

const STATUSES = ['Todo', 'In Progress', 'Review', 'Done'];
const PRIORITY_RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const COL_THEME = {
  'Todo': 'border-t-slate-400',
  'In Progress': 'border-t-[#3b4872]',
  'Review': 'border-t-amber-500',
  'Done': 'border-t-emerald-500',
};
const PRIORITY_COLOR = {
  Critical: 'bg-red-50 text-red-700 border border-red-200',
  High: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  Medium: 'bg-amber-50 text-amber-700 border border-amber-200',
  Low: 'bg-slate-50 text-slate-600 border border-slate-200',
};

/**
 * Cross-module Kanban task board.
 * Filterable by project, department.
 * Cards can link to PO / NCR / RFI / Drawing via linked_module + linked_id.
 */
export default function KanbanBoard({ projectId = null, department = null, fullScreen = false }) {
  const [board, setBoard] = useState({ columns: { Todo: [], 'In Progress': [], Review: [], Done: [] }, total: 0 });
  const [adding, setAdding] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState({ project: projectId || '', department: department || '' });
  const [projects, setProjects] = useState([]);

  const load = async () => {
    try {
      const params = {};
      if (filter.project) params.project_id = filter.project;
      if (filter.department) params.department = filter.department;
      const b = await apiGet('/task-board', params);
      setBoard(b);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.project, filter.department]);
  useEffect(() => { if (fullScreen) apiGet('/projects').then(setProjects).catch(() => {}); }, [fullScreen]);

  const onDragStart = (e, task) => { e.dataTransfer.setData('task_id', task.id); e.dataTransfer.setData('from', task.status); };
  const onDrop = async (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('task_id');
    const from = e.dataTransfer.getData('from');
    if (!id || from === status) return;
    try {
      await apiPut(`/tasks/${id}/move`, { status });
      toast.success(`Moved to ${status}`);
      load();
    } catch { toast.error('Move failed'); }
  };

  const create = async (data) => {
    try {
      const deps = data._pendingDeps || [];
      delete data._pendingDeps;
      const created = await apiPost('/tasks', {
        ...data,
        status: data.status || 'Todo',
        priority_rank: PRIORITY_RANK[data.priority || 'Medium'] || 2,
        project_id: data.project_id || filter.project || null,
        department: data.department || filter.department || null,
        comments: [],
      });
      // Add any cross-department dependencies the user declared
      for (const d of deps) {
        try { await apiPost(`/tasks/${created.id}/dependencies`, d); } catch {}
      }
      toast.success(deps.length ? `Task + ${deps.length} dependency request(s) created` : 'Task created');
      setAdding(false); load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="kpi-overline">Task Board</div>
          <h3 className="font-display font-bold text-base mt-1">{board.total} tasks {projectId ? '· this project' : department ? `· ${department}` : '· cross-module'}</h3>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          {fullScreen && (
            <>
              <select value={filter.project} onChange={e => setFilter(f => ({ ...f, project: e.target.value }))} data-testid="task-filter-project"
                className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="">All Projects</option>
                {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
              </select>
              <select value={filter.department} onChange={e => setFilter(f => ({ ...f, department: e.target.value }))} data-testid="task-filter-dept"
                className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="">All Departments</option>
                {['projects', 'engineering', 'procurement', 'finance', 'quality', 'hse', 'subcontractors', 'hr'].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </>
          )}
          <button onClick={() => setAdding(true)} data-testid="task-add-btn"
            className="px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-semibold flex items-center gap-1.5 hover:bg-[#2c3658]">
            <Plus size={13} /> New Task
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {STATUSES.map(s => (
          <div key={s} data-testid={`kanban-col-${s.replace(/\s+/g, '-').toLowerCase()}`}
            onDragOver={e => e.preventDefault()} onDrop={e => onDrop(e, s)}
            className={`bg-slate-50/60 border border-slate-200 border-t-2 ${COL_THEME[s]} rounded-sm flex flex-col min-h-[400px]`}>
            <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
              <span className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-slate-600">{s}</span>
              <span className="text-[10px] font-display font-bold tabular-nums bg-white border border-slate-200 px-1.5 rounded-sm">{board.columns[s]?.length || 0}</span>
            </div>
            <div className="p-2 space-y-2 flex-1">
              {(board.columns[s] || []).map(t => (
                <TaskCard key={t.id} task={t} onClick={() => setDetail(t)} onDragStart={onDragStart} />
              ))}
              {(board.columns[s] || []).length === 0 && <div className="text-[10px] text-slate-400 uppercase tracking-widest font-display text-center py-6">Drop here</div>}
            </div>
          </div>
        ))}
      </div>

      {adding && <TaskCreateModal onClose={() => setAdding(false)} onCreate={create} projectIdLocked={projectId} departmentLocked={department} />}
      {detail && <TaskDetailModal task={detail} onClose={() => setDetail(null)} onChanged={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function TaskCard({ task, onClick, onDragStart }) {
  return (
    <div draggable onDragStart={e => onDragStart(e, task)} onClick={onClick}
      data-testid={`task-card-${task.id}`}
      className="bg-white border border-slate-200 rounded-sm p-2.5 cursor-pointer hover:border-[#3b4872]/60 hover:shadow-sm transition-all">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="text-[13px] font-display font-semibold leading-snug flex-1 flex items-start gap-1.5">
          <span>{task.title}</span>
        </div>
        <span className={`tag ${PRIORITY_COLOR[task.priority || 'Medium']} text-[9px]`}>{task.priority || 'Medium'}</span>
      </div>
      {task.assignee && <div className="text-[11px] text-slate-500 mb-1.5">@ {task.assignee}</div>}
      <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
        {task.due_date && <span className="flex items-center gap-1"><Calendar size={10} />{task.due_date}</span>}
        {task.linked_module && <span className="flex items-center gap-1 text-[#3b4872]"><Link2 size={10} />{task.linked_module}</span>}
        {(task.comments || []).length > 0 && <span className="flex items-center gap-1"><MessageSquare size={10} />{(task.comments || []).length}</span>}
        {task.department && <span className="flex items-center gap-1 uppercase tracking-widest font-display font-bold">{task.department}</span>}
      </div>
    </div>
  );
}

function TaskCreateModal({ onClose, onCreate, projectIdLocked, departmentLocked }) {
  const { user } = useAuth() || {};
  const currentUserName = user?.name || user?.email || '';
  const canOverrideOwner = user?.role === 'super_admin' || user?.department === 'MD';
  const [form, setForm] = useState({
    priority: 'Medium',
    status: 'Todo',
    tags: [],
    project_id: projectIdLocked || '',
    department: departmentLocked || '',
    owner: currentUserName,
    owner_id: user?.user_id || '',
    assigned_to: '',
    linked_entity: null,
    _pendingDeps: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [projects, setProjects] = useState([]);
  const [depDraft, setDepDraft] = useState({ to_department: '', description: '', priority: 'Medium', due_date: '' });
  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => {}); }, []);

  const addDep = () => {
    if (!depDraft.to_department || !depDraft.description.trim()) {
      toast.error('Pick a department and describe what is needed'); return;
    }
    setForm({ ...form, _pendingDeps: [...(form._pendingDeps || []), { ...depDraft, description: depDraft.description.trim() }] });
    setDepDraft({ to_department: '', description: '', priority: 'Medium', due_date: '' });
  };
  const removeDep = (idx) => setForm({ ...form, _pendingDeps: form._pendingDeps.filter((_, i) => i !== idx) });

  const submit = (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title required'); return; }
    if (!form.due_date) { toast.error('Due date & time required'); return; }
    const payload = { ...form };
    // Normalize linked entity into flat fields
    if (form.linked_entity) {
      payload.linked_entity_type = form.linked_entity.type;
      payload.linked_entity_id = form.linked_entity.id;
      payload.linked_entity_label = form.linked_entity.label;
    }
    delete payload.linked_entity;
    onCreate(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-xl p-5 grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div>
          <h3 className="font-display font-bold text-xl mt-1">Task</h3>
        </div>
        <div className="col-span-2">
          <input value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Task title" data-testid="task-title-input"
            className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
        </div>
        <div className="col-span-2">
          <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={3}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:outline-none" />
        </div>
        {/* Owner — read-only chip for normal users */}
        <div>
          <label className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 flex items-center gap-1">
            Owner
            {!canOverrideOwner && <Lock size={10} className="text-slate-400" />}
          </label>
          {canOverrideOwner ? (
            <EmployeePicker value={form.owner || currentUserName} onChange={v => setForm({ ...form, owner: v })} testid="kanban-owner-picker" />
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-sm bg-slate-50 text-sm text-slate-600" data-testid="kanban-owner-readonly">
              <UserIcon size={13} className="text-slate-400" />
              <span className="truncate">{currentUserName || '—'}</span>
              <span className="ml-auto text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-400">You</span>
            </div>
          )}
        </div>
        {/* Assigned To */}
        <div>
          <label className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 block">Assigned To</label>
          <EmployeePicker value={form.assigned_to || ''} onChange={v => setForm({ ...form, assigned_to: v, assignee: v })} placeholder="Search team member…" testid="kanban-assigned-picker" />
        </div>
        {/* Due date + time */}
        <div className="col-span-2">
          <label className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 block">Due Date &amp; Time *</label>
          <input type="datetime-local" value={form.due_date || ''} onChange={e => setForm({ ...form, due_date: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" required data-testid="kanban-due-input" />
        </div>
        <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
          {['Critical', 'High', 'Medium', 'Low'].map(p => <option key={p}>{p}</option>)}
        </select>
        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        {!projectIdLocked && (
          <select value={form.project_id || ''} onChange={e => setForm({ ...form, project_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white col-span-2">
            <option value="">— No project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
          </select>
        )}
        {!departmentLocked && (
          <select value={form.department || ''} onChange={e => setForm({ ...form, department: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white col-span-2">
            <option value="">— No department —</option>
            {['projects', 'engineering', 'procurement', 'finance', 'quality', 'hse', 'subcontractors', 'hr'].map(d => <option key={d}>{d}</option>)}
          </select>
        )}
        {/* Link To — department-aware */}
        <div className="col-span-2">
          <label className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500 mb-1 block">Link to record</label>
          <LinkedEntityPicker
            value={form.linked_entity}
            onChange={(sel) => setForm({ ...form, linked_entity: sel })}
            department={user?.department}
            role={user?.role}
            testid="kanban-link-picker"
          />
        </div>
        <div className="col-span-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {(form.tags || []).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-[#3b4872] text-[10px] rounded-sm font-display font-bold uppercase tracking-widest">
                {t}
                <button type="button" onClick={() => setForm({ ...form, tags: form.tags.filter((_, j) => j !== i) })}><X size={10} /></button>
              </span>
            ))}
            <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && tagInput.trim()) { e.preventDefault(); setForm({ ...form, tags: [...(form.tags || []), tagInput.trim()] }); setTagInput(''); }
            }} placeholder="Add tag (Enter)" className="px-2 py-1 border border-slate-200 rounded-sm text-xs flex-1 min-w-[100px]" />
          </div>
        </div>
        <div className="col-span-2 pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#3b4872]">Cross-Department Dependencies</div>
            <span className="text-[10px] text-slate-500">{(form._pendingDeps || []).length} added</span>
          </div>
          {(form._pendingDeps || []).length > 0 && (
            <div className="space-y-1.5 mb-2">
              {form._pendingDeps.map((d, i) => (
                <div key={i} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5 text-[11px]" data-testid={`pending-dep-${i}`}>
                  <span className="font-display font-bold uppercase tracking-wider text-[#3b4872] shrink-0">{d.to_department}</span>
                  <span className="flex-1 text-slate-700">{d.description}</span>
                  <span className="text-slate-500 shrink-0">{d.due_date || 'no due'}</span>
                  <button type="button" onClick={() => removeDep(i)} className="text-rose-600 hover:text-rose-800"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-12 gap-1.5">
            <select value={depDraft.to_department} onChange={(e) => setDepDraft({ ...depDraft, to_department: e.target.value })}
              className="col-span-3 px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="dep-draft-dept">
              <option value="">Dept…</option>
              {['projects', 'engineering', 'procurement', 'finance', 'quality', 'hse', 'subcontractors', 'hr'].map((d) => <option key={d}>{d}</option>)}
            </select>
            <input value={depDraft.description} onChange={(e) => setDepDraft({ ...depDraft, description: e.target.value })}
              placeholder="What is needed from this dept?"
              className="col-span-5 px-2 py-1.5 border border-slate-300 rounded-sm text-xs" data-testid="dep-draft-desc" />
            <input type="date" value={depDraft.due_date} onChange={(e) => setDepDraft({ ...depDraft, due_date: e.target.value })}
              className="col-span-2 px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
            <button type="button" onClick={addDep} className="col-span-2 px-2 py-1.5 bg-[#3b4872] text-white rounded-sm text-[11px] font-display font-bold uppercase tracking-wider hover:bg-[#2f3a5d]" data-testid="dep-draft-add">+ Add</button>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">Each dependency lands as a pending request in the receiving department's inbox.</p>
        </div>
        <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
          <button type="submit" data-testid="task-create-submit" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold">Create Task</button>
        </div>
      </form>
    </div>
  );
}

const DEP_STATUS_TAG = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200',
  assigned: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
  in_progress: 'bg-sky-50 text-sky-700 border border-sky-200',
  done: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  dismissed: 'bg-rose-50 text-rose-700 border border-rose-200',
  rerouted: 'bg-slate-100 text-slate-600 border border-slate-200',
};

function TaskDetailModal({ task, onClose, onChanged }) {
  const [comment, setComment] = useState('');
  const [local, setLocal] = useState(task);
  const [showDelegate, setShowDelegate] = useState(false);
  const [delegateEmail, setDelegateEmail] = useState('');
  const [delegateNote, setDelegateNote] = useState('');
  const [depDraft, setDepDraft] = useState({ to_department: '', description: '', priority: 'Medium', due_date: '' });
  const [addingDep, setAddingDep] = useState(false);

  const addDependency = async () => {
    if (!depDraft.to_department || !depDraft.description.trim()) {
      toast.error('Pick a department and describe what is needed'); return;
    }
    setAddingDep(true);
    try {
      const res = await apiPost(`/tasks/${task.id}/dependencies`, {
        ...depDraft,
        description: depDraft.description.trim(),
      });
      const newDep = res?.dependency;
      setLocal(prev => ({ ...prev, dependencies: [...(prev.dependencies || []), newDep] }));
      setDepDraft({ to_department: '', description: '', priority: 'Medium', due_date: '' });
      toast.success('Dependency requested');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add dependency');
    } finally {
      setAddingDep(false);
    }
  };

  const move = async (status) => {
    try {
      const upd = await apiPut(`/tasks/${task.id}/move`, { status });
      setLocal(upd); toast.success('Status updated'); onChanged();
    } catch { toast.error('Failed'); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      const upd = await apiPost(`/tasks/${task.id}/comments`, { text: comment });
      setLocal(upd); setComment('');
    } catch { toast.error('Failed'); }
  };

  // approve / reject / delegate
  const approve = async () => {
    const note = prompt('Optional approval note:');
    try {
      const upd = await apiPost(`/tasks/${task.id}/approve`, { note: note || '' });
      setLocal(upd); toast.success('Task approved'); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };
  const reject = async () => {
    const note = prompt('Reason for rejection (required):');
    if (!note?.trim()) { toast.error('Reason required'); return; }
    try {
      const upd = await apiPost(`/tasks/${task.id}/reject`, { note });
      setLocal(upd); toast.success('Task rejected — bounced to In Progress'); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
  };
  const delegate = async () => {
    if (!delegateEmail.trim()) { toast.error('Pick a target user'); return; }
    try {
      const upd = await apiPost(`/tasks/${task.id}/delegate`, {
        to_email: delegateEmail.trim().toLowerCase(),
        to_name: delegateEmail.split('@')[0],
        note: delegateNote.trim(),
      });
      setLocal(upd); setShowDelegate(false); setDelegateEmail(''); setDelegateNote('');
      toast.success('Task delegated'); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delegate failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`tag ${PRIORITY_COLOR[local.priority || 'Medium']}`}>{local.priority || 'Medium'}</span>
              {local.department && <span className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">{local.department}</span>}
            </div>
            <h3 className="font-display font-bold text-xl mt-2">{local.title}</h3>
            {local.description && <div className="text-sm text-slate-600 mt-2 whitespace-pre-line">{local.description}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3 text-xs">
          <Field label="Assignee" value={local.assignee} />
          <Field label="Due Date" value={local.due_date} icon={Calendar} />
          {local.tags?.length > 0 && <div className="col-span-2"><div className="kpi-overline mb-1.5">Tags</div><div className="flex flex-wrap gap-1.5">{local.tags.map((t, i) => <span key={i} className="px-2 py-0.5 bg-slate-100 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest"><Tag size={9} className="inline mr-1" />{t}</span>)}</div></div>}
        </div>
        <div className="px-5 pb-3 border-b border-slate-100">
          <div className="kpi-overline mb-2 flex items-center gap-2">
            Actions
            {local.approved && <span className="tag bg-emerald-50 text-emerald-700 text-[9px]">APPROVED</span>}
            {local.rejection_reason && <span className="tag bg-rose-50 text-rose-700 text-[9px]">REJECTED · {local.rejection_reason.slice(0, 40)}</span>}
            {local.delegated_at && <span className="tag bg-amber-50 text-amber-700 text-[9px]">DELEGATED</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={approve} data-testid="task-approve-btn"
              className="px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm hover:bg-emerald-700">
              ✓ Approve
            </button>
            <button onClick={reject} data-testid="task-reject-btn"
              className="px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider border border-rose-500 text-rose-600 rounded-sm hover:bg-rose-50">
              ✗ Reject
            </button>
            <button onClick={() => setShowDelegate(s => !s)} data-testid="task-delegate-btn"
              className="px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider border border-[#3b4872] text-[#3b4872] rounded-sm hover:bg-indigo-50">
              ↪ Delegate
            </button>
          </div>
          {showDelegate && (
            <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-sm space-y-2">
              <input type="email" value={delegateEmail} onChange={e => setDelegateEmail(e.target.value)}
                placeholder="Assign to (email)…"
                className="w-full px-3 py-1.5 border border-slate-300 rounded-sm text-xs"
                data-testid="delegate-email-input" />
              <input type="text" value={delegateNote} onChange={e => setDelegateNote(e.target.value)}
                placeholder="Delegation note (optional)…"
                className="w-full px-3 py-1.5 border border-slate-300 rounded-sm text-xs" />
              <button onClick={delegate} data-testid="delegate-confirm-btn"
                className="px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856]">
                Confirm Delegate
              </button>
            </div>
          )}
        </div>
        <div className="px-5 pb-5">
          <div className="kpi-overline mb-2">Move to</div>
          <div className="grid grid-cols-4 gap-1.5">
            {STATUSES.map(s => (
              <button key={s} onClick={() => move(s)} disabled={s === local.status} data-testid={`task-move-${s.replace(/\s+/g, '-').toLowerCase()}`}
                className={`px-2 py-1.5 text-[11px] font-display font-bold rounded-sm border ${s === local.status ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-300 hover:border-[#3b4872] hover:text-[#3b4872]'} disabled:cursor-default`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="px-5 pb-5 border-b border-slate-100">
          <div className="kpi-overline mb-2 flex items-center gap-1">
            <Link2 size={11} /> Cross-Department Dependencies · {(local.dependencies || []).length}
          </div>
          {(local.dependencies || []).length > 0 ? (
            <div className="space-y-1.5 mb-3">
              {local.dependencies.map((d, i) => (
                <div key={d.id || i} data-testid={`task-dep-${i}`}
                  className="bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-2 text-[11px]">
                  <div className="flex items-start gap-2">
                    <span className="font-display font-bold uppercase tracking-wider text-[#3b4872] shrink-0">{d.to_department}</span>
                    <span className="flex-1 text-slate-700">{d.description}</span>
                    <span className={`tag ${DEP_STATUS_TAG[d.status] || DEP_STATUS_TAG.pending} text-[9px] uppercase`}>{d.status || 'pending'}</span>
                  </div>
                  {(d.assigned_to || d.due_date) && (
                    <div className="mt-1 text-[10px] text-slate-500 flex gap-3">
                      {d.assigned_to && <span>→ {d.assigned_to}</span>}
                      {d.due_date && <span>Due {d.due_date}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-slate-400 italic mb-2">No dependencies on other departments.</div>
          )}
          <div className="grid grid-cols-12 gap-1.5">
            <select value={depDraft.to_department} onChange={e => setDepDraft({ ...depDraft, to_department: e.target.value })}
              className="col-span-3 px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="task-dep-dept">
              <option value="">Dept…</option>
              {['projects', 'engineering', 'procurement', 'finance', 'quality', 'hse', 'subcontractors', 'hr'].map(d => <option key={d}>{d}</option>)}
            </select>
            <input value={depDraft.description} onChange={e => setDepDraft({ ...depDraft, description: e.target.value })}
              placeholder="What is needed from this dept?"
              className="col-span-5 px-2 py-1.5 border border-slate-300 rounded-sm text-xs" data-testid="task-dep-desc" />
            <input type="date" value={depDraft.due_date} onChange={e => setDepDraft({ ...depDraft, due_date: e.target.value })}
              className="col-span-2 px-2 py-1.5 border border-slate-300 rounded-sm text-xs" />
            <button onClick={addDependency} disabled={addingDep}
              className="col-span-2 px-2 py-1.5 bg-[#3b4872] text-white rounded-sm text-[11px] font-display font-bold uppercase tracking-wider hover:bg-[#2f3a5d] disabled:opacity-50"
              data-testid="task-dep-add-btn">
              {addingDep ? '…' : '+ Add'}
            </button>
          </div>
        </div>
        <div className="px-5 pb-5">
          <div className="kpi-overline mb-2 flex items-center gap-1"><MessageSquare size={11} /> Comments · {(local.comments || []).length}</div>
          <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
            {(local.comments || []).map(c => (
              <div key={c.id} className="bg-slate-50 border border-slate-200 rounded-sm p-2.5">
                <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                  <span className="font-display font-bold uppercase tracking-widest">{c.author}</span>
                  <span>{c.at?.slice(0, 16).replace('T', ' ')}</span>
                </div>
                <div className="text-xs text-slate-700">{c.text}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Add comment…" data-testid="task-comment-input"
              onKeyDown={e => e.key === 'Enter' && addComment()}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            <button onClick={addComment} data-testid="task-comment-submit" className="px-4 py-2 text-sm bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm font-display font-semibold">Post</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="kpi-overline mb-1">{label}</div>
      <div className="font-display font-semibold text-sm flex items-center gap-1">{Icon && <Icon size={12} className="text-slate-400" />}{value || '—'}</div>
    </div>
  );
}
